import { useRef, useState } from 'react';
import { Download, Upload, FileSpreadsheet, Loader2, AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';

/**
 * Bulk Excel export/import for any registered entity.
 *
 * Flow the user sees:
 *   Export (or Template) -> edit in Excel -> Upload -> preview of exactly what
 *   will change -> Confirm. A file with any invalid cell is refused outright,
 *   listing every problem with its row and column.
 */

interface CellError {
  row: number;
  column: string;
  value: string;
  message: string;
}

interface RowChange {
  row: number;
  action: 'create' | 'update' | 'unchanged';
  id: string | null;
  label: string;
  changes: Array<{ column: string; from: unknown; to: unknown }>;
}

export interface ImportReport {
  ok: boolean;
  entity: string;
  fileName?: string;
  totalRows: number;
  toCreate: number;
  toUpdate: number;
  unchanged: number;
  skipped?: number;
  partial?: boolean;
  filled?: number;
  skippedRows?: Array<{ row: number; reason: string; missing: string[] }>;
  filledRows?: Array<{ row: number; filled: string[] }>;
  matchedExisting?: number;
  matchedOn?: string;
  ignoredColumns?: string[];
  errors: CellError[];
  changes: RowChange[];
  batchId?: string;
}

interface ExcelToolbarProps {
  /** Registry key, e.g. "vehicles" or "trips". */
  entity: string;
  /** Human label used in messages. */
  title: string;
  /** Called after a successful import so the page can refetch. */
  onImported?: () => void;
  /**
   * Limit the export to a period, inclusive. Given by a page that already has a
   * date filter on screen, so Export downloads what is being looked at rather
   * than every row ever recorded.
   */
  exportFrom?: string;
  exportTo?: string;
  /**
   * Offer a division picker for this upload. Only for entities that belong to
   * one - drivers and vehicles. The whole file lands on the chosen side, which
   * beats typing it into every row.
   */
  askProject?: boolean;
  /**
   * Show only the export button, hiding Blank template and Upload Excel.
   *
   * For screens where reading the data out is wanted but bulk-writing it back
   * is not - the drivers list, where an upload can rewrite a whole roster and
   * the import screen is the place for that.
   */
  exportOnly?: boolean;
}

/** The three sides of the business, as stored in operational_projects.code. */
const PROJECTS = [
  { code: 'PRJ-NEOM', label: 'NEOM' },
  { code: 'PRJ-RIYADH-WATER', label: 'Riyadh Water' },
  { code: 'PRJ-DUMPERS', label: 'Dumpers' },
  { code: 'PRJ-RIYADH', label: 'Flatbed' },
];

async function downloadFile(url: string, fallbackName: string) {
  const res = await apiClient.get(url, { responseType: 'blob' });
  const disposition = String(res.headers['content-disposition'] ?? '');
  const match = disposition.match(/filename="?([^"]+)"?/);
  const name = match?.[1] ?? fallbackName;
  const blobUrl = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

export function ExcelToolbar({ entity, title, onImported, askProject, exportOnly, exportFrom, exportTo }: ExcelToolbarProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<null | 'export' | 'template' | 'validate' | 'import'>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const handleExport = async (kind: 'export' | 'template') => {
    setBusy(kind); setError(null); setDone(null);
    try {
      // The period applies to the data export only. A blank template has no
      // rows to filter, and narrowing it would just produce the same file.
      const range = new URLSearchParams();
      if (kind === 'export' && exportFrom) range.set('from', exportFrom);
      if (kind === 'export' && exportTo) range.set('to', exportTo);
      const q = range.toString();
      const stamp = q ? `-${exportFrom ?? 'start'}_to_${exportTo ?? 'today'}` : '';
      await downloadFile(
        `/api/v1/excel/${entity}/${kind}${q ? `?${q}` : ''}`,
        `jasrhub-${entity}${stamp}.xlsx`,
      );
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally { setBusy(null); }
  };

  // Partial: accept a sheet that only carries some of the columns, and set
  // aside rows it cannot fully create instead of refusing the whole file.
  const [partial, setPartial] = useState(false);
  /** Deliberately bring back rows whose tickets were deleted. */
  const [restoreDeleted, setRestoreDeleted] = useState(false);
  const [fill, setFill] = useState(false);
  // Which side of the business this file belongs to. Blank means "whatever the
  // sheet says", which for a sheet with no project column means no division at
  // all - and a driver with no division is invisible from inside every one.
  const [project, setProject] = useState('');
  const qs = (() => {
    const parts: string[] = [];
    if (partial) parts.push('partial=1');
    if (restoreDeleted) parts.push('restoreDeleted=true');
    if (partial && fill) parts.push('fill=1');
    if (askProject && project) parts.push(`project=${encodeURIComponent(project)}`);
    return parts.length ? `?${parts.join('&')}` : '';
  })();

  const handleFile = async (file: File) => {
    setBusy('validate'); setError(null); setDone(null); setReport(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await apiClient.post<ImportReport>(`/api/v1/excel/${entity}/validate${qs}`, fd);
      setReport(data);
      setPendingFile(file);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally { setBusy(null); }
  };

  const retryRestoringDeleted = async () => {
    if (!pendingFile) return;
    setRestoreDeleted(true);
    setBusy('validate'); setError(null);
    try {
      const parts = ['restoreDeleted=true'];
      if (partial) parts.push('partial=1');
      if (partial && fill) parts.push('fill=1');
      if (askProject && project) parts.push(`project=${encodeURIComponent(project)}`);
      const fd = new FormData();
      fd.append('file', pendingFile);
      const { data } = await apiClient.post<ImportReport>(
        `/api/v1/excel/${entity}/validate?${parts.join('&')}`, fd);
      setReport(data);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setBusy(null); }
  };

  const handleConfirm = async () => {
    if (!pendingFile) return;
    setBusy('import'); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', pendingFile);
      const { data } = await apiClient.post<ImportReport>(`/api/v1/excel/${entity}/import${qs}`, fd);
      setReport(null);
      setPendingFile(null);
      setDone(
        `${data.toCreate} created, ${data.toUpdate} updated`
        + (data.skipped ? `, ${data.skipped} row(s) skipped — fill those in by hand.` : '.'),
      );
      onImported?.();
    } catch (e) {
      // 422 means the file was rejected; show the detailed report again.
      const resp = (e as { response?: { status?: number; data?: ImportReport } }).response;
      if (resp?.status === 422 && resp.data) setReport(resp.data);
      else setError(extractErrorMessage(e));
    } finally { setBusy(null); }
  };

  const close = () => { setReport(null); setPendingFile(null); };

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <Button type="button" variant="outline" size="sm" disabled={busy !== null} onClick={() => handleExport('export')}>
          {busy === 'export' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export Excel
        </Button>
        {!exportOnly && (
          <>
            <Button type="button" variant="outline" size="sm" disabled={busy !== null} onClick={() => handleExport('template')}>
              {busy === 'template' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
              Blank template
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={busy !== null} onClick={() => fileRef.current?.click()}>
              {busy === 'validate' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload Excel
            </Button>
          </>
        )}
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="sr-only"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
        />

        {/* For a sheet that only has some of the information. */}
        <label className="inline-flex items-center gap-2 text-xs text-muted-foreground cursor-pointer ms-1">
          <input
            type="checkbox"
            checked={partial}
            onChange={(e) => { setPartial(e.target.checked); setReport(null); setPendingFile(null); setDone(null); }}
            className="cursor-pointer"
          />
          Take what the file has
        </label>

        {/* Without this, a file whose rows were deleted on purpose imports as
            "0 rows" and reads like a broken upload. */}
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={restoreDeleted}
            onChange={(e) => { setRestoreDeleted(e.target.checked); setReport(null); setPendingFile(null); setDone(null); }}
            className="cursor-pointer"
          />
          Bring back rows I deleted earlier
        </label>

        {/* Which side the uploaded roster belongs to. */}
        {askProject && (
          <label className="inline-flex items-center gap-2 text-xs text-muted-foreground ms-1">
            Side
            <select
              value={project}
              onChange={(e) => { setProject(e.target.value); setReport(null); setPendingFile(null); setDone(null); }}
              className="rounded border border-border bg-background px-2 py-1 text-xs text-foreground"
            >
              <option value="">As the file says</option>
              {PROJECTS.map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}
            </select>
          </label>
        )}
      </div>

      {askProject && (
        <p className="mt-2 text-xs text-muted-foreground rounded border border-border bg-muted/30 px-3 py-2">
          {project
            ? <>Everything in this file goes to <b className="text-foreground">{PROJECTS.find((p) => p.code === project)?.label}</b>. A row that names its own division keeps it.</>
            : <>Pick a side and the whole file is filed there. Left as it is, rows that carry no division stay unfiled — and an unfiled driver does not show up inside NEOM, Dumpers or Flatbed.</>}
        </p>
      )}

      {partial && (
        <div className="mt-2 space-y-2">
          <p className="text-xs text-muted-foreground rounded border border-border bg-muted/30 px-3 py-2">
            The sheet may leave columns out — anything missing is left alone. New rows that still
            lack something essential are listed for you to finish by hand instead of stopping the
            upload. Wrong values are still refused.
          </p>
          <label className="inline-flex items-start gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={fill}
              onChange={(e) => { setFill(e.target.checked); setReport(null); setPendingFile(null); setDone(null); }}
              className="cursor-pointer mt-0.5"
            />
            <span className="text-muted-foreground">
              <b className="text-foreground">Create them anyway, filling the gaps.</b> Rows that
              cannot be created get stand-in values instead of being skipped, and are tagged so you
              can find them. A stand-in never reads as valid — a driver with no licence on file
              shows as <b className="text-foreground">expired</b>, not as compliant.
            </span>
          </label>
        </div>
      )}

      {error && (
        <p className="mt-2 text-sm text-destructive rounded border border-destructive/30 bg-destructive/10 px-3 py-2" role="alert">
          {error}
        </p>
      )}
      {done && (
        <p className="mt-2 text-sm text-emerald-700 dark:text-emerald-400 rounded border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 inline-flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" /> {done}
        </p>
      )}

      {report && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
            <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
              <div>
                <h2 className="font-display text-xl text-foreground">
                  {report.ok ? `Confirm ${title} upload` : 'File rejected'}
                </h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {report.fileName ?? 'uploaded file'} - {report.totalRows} data row(s)
                </p>
              </div>
              <button type="button" onClick={close} className="text-muted-foreground hover:text-foreground" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {!report.ok ? (
                <>
                  <div className="rounded border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive inline-flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      Nothing was saved. Fix every problem below in the spreadsheet, then upload it again.
                      The import is all-or-nothing on purpose.
                    </span>
                  </div>
                  <table className="w-full text-xs border border-border rounded overflow-hidden">
                    <thead className="bg-muted/40 text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2 font-normal w-20">Excel row</th>
                        <th className="text-left px-3 py-2 font-normal w-48">Column</th>
                        <th className="text-left px-3 py-2 font-normal w-40">Value</th>
                        <th className="text-left px-3 py-2 font-normal">Problem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.errors.map((e, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-3 py-2 font-mono">{e.row || '-'}</td>
                          <td className="px-3 py-2">{e.column || '-'}</td>
                          <td className="px-3 py-2 font-mono text-muted-foreground">{e.value || '(blank)'}</td>
                          <td className="px-3 py-2">{e.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <>
                  <div className={`grid gap-3 ${(report.skipped ?? 0) > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
                    <Stat label="Will be created" value={report.toCreate} tone="emerald" />
                    <Stat label="Will be updated" value={report.toUpdate} tone="amber" />
                    <Stat label="Unchanged" value={report.unchanged} tone="muted" />
                    {(report.skipped ?? 0) > 0 && (
                      <Stat label="Skipped" value={report.skipped ?? 0} tone="amber" />
                    )}
                  </div>

                  {(report.filled ?? 0) > 0 && (
                    <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-700">
                      <b>{report.filled} row(s) will be created with stand-in values</b> for the
                      columns the sheet did not carry. They are tagged in Notes, and anything with a
                      date stand-in will show as expired until you correct it.
                    </div>
                  )}

                  {/* A file from outside carries no ID, so without this the
                      user has no way to tell an update from a fresh create. */}
                  {(report.matchedExisting ?? 0) > 0 && (
                    <div className="rounded border border-sky-500/40 bg-sky-500/10 p-3 text-xs text-sky-700">
                      <b>{report.matchedExisting} row(s) already exist here</b> and will be updated
                      rather than added again, matched on {report.matchedOn ?? 'their identifier'}.
                      Anything you have since filled in by hand is left alone.
                    </div>
                  )}

                  {/* Named one by one, because these are the rows you have to
                      go and finish yourself. */}
                  {(report.skippedRows?.length ?? 0) > 0 && (
                    <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3">
                      <div className="text-xs font-medium text-amber-700 mb-1">
                        {report.skippedRows!.length} row(s) could not be created from this file
                      </div>
                      <ul className="text-xs text-muted-foreground space-y-0.5 max-h-40 overflow-y-auto">
                        {report.skippedRows!.slice(0, 50).map((s) => (
                          <li key={s.row}>Row {s.row} — {s.reason}</li>
                        ))}
                      </ul>
                      {report.skippedRows!.length > 50 && (
                        <div className="text-[11px] text-muted-foreground mt-1">
                          …and {report.skippedRows!.length - 50} more.
                        </div>
                      )}
                    </div>
                  )}
                  {report.toCreate === 0 && report.toUpdate === 0 ? (
                    /*
                     * "Nothing to do" is only true when the sheet really does
                     * match. When every row was skipped because its delivery
                     * ticket was deleted on purpose, saying "matches" sends the
                     * reader looking for a problem in the file that is not there.
                     */
                    (report as { skippedDeleted?: number }).skippedDeleted ? (
                      <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                        <div className="font-medium text-amber-700 dark:text-amber-400">
                          {(report as { skippedDeleted?: number }).skippedDeleted} row(s) skipped — these trips were deleted on purpose
                        </div>
                        <div className="text-muted-foreground mt-1">
                          Their delivery tickets are on the deleted list, so re-uploading cannot bring them
                          back by accident. If you deleted them in order to replace them with corrected rows,
                          bring them back here.
                        </div>
                        <Button size="sm" variant="outline" className="mt-2"
                          disabled={busy !== null}
                          onClick={retryRestoringDeleted}>
                          {busy === 'validate' ? 'Checking…' : 'Bring these rows back'}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Nothing to do - the sheet matches what is already stored.
                      </p>
                    )
                  ) : (
                    <div className="rounded border border-border overflow-hidden">
                      <div className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground bg-muted/30 border-b border-border">
                        Exactly what will change
                      </div>
                      <table className="w-full text-xs">
                        <tbody>
                          {report.changes.filter((c) => c.action !== 'unchanged').map((c) => (
                            <tr key={c.row} className="border-t border-border align-top">
                              <td className="px-3 py-2 font-mono w-16 text-muted-foreground">{c.row}</td>
                              <td className="px-3 py-2 w-24">
                                <span className={c.action === 'create'
                                  ? 'text-emerald-700 dark:text-emerald-400'
                                  : 'text-amber-700 dark:text-amber-400'}>
                                  {c.action}
                                </span>
                              </td>
                              <td className="px-3 py-2 font-medium">{c.label}</td>
                              <td className="px-3 py-2 text-muted-foreground">
                                {c.action === 'create' ? 'new record' : c.changes.map((d) => (
                                  <div key={d.column}>
                                    <span className="font-mono">{d.column}</span>{': '}
                                    <span className="line-through opacity-60">{String(d.from ?? '(blank)')}</span>
                                    {' -> '}
                                    <span className="text-foreground">{String(d.to ?? '(blank)')}</span>
                                  </div>
                                ))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-end gap-2 p-5 border-t border-border bg-muted/20">
              <Button type="button" variant="outline" onClick={close} disabled={busy === 'import'}>
                {report.ok ? 'Cancel' : 'Close'}
              </Button>
              {report.ok && (report.toCreate > 0 || report.toUpdate > 0) && (
                <Button type="button" variant="gold" onClick={handleConfirm} disabled={busy === 'import'}>
                  {busy === 'import' && <Loader2 className="h-4 w-4 animate-spin" />}
                  Apply {report.toCreate + report.toUpdate} change(s)
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: 'emerald' | 'amber' | 'muted' }) {
  const color = tone === 'emerald'
    ? 'text-emerald-700 dark:text-emerald-400'
    : tone === 'amber'
      ? 'text-amber-700 dark:text-amber-400'
      : 'text-muted-foreground';
  return (
    <div className="rounded-md border border-border p-3 bg-muted/10">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-2xl font-mono font-light mt-0.5 ${color}`}>{value}</div>
    </div>
  );
}
